// ============================================================
// diagnostic-content.js
// Transformation des résultats du moteur en contenus utilisateur
//
// Structure narrative des résultats :
//   1. "Nous avons analysé votre peau."
//   2. "Voici ce que nous avons observé." (piliers)
//   3. "Voilà ce que cela signifie." (synthèse)
//   4. "Voici la routine qui répond à ces besoins." (produits)
//   5. "Pour aller plus loin." (articles)
// ============================================================


// ============================================================
// SECTION 1 — DESCRIPTIONS DES PILIERS
// ============================================================

const PILIERS = {
  B: {
    id:     'B',
    label:  'Barrière & confort',
    labelRoutine: 'la fonction barrière',
    labelNiveau: 'de la fonction barrière',
    resume: 'Votre peau exprime un besoin de soutien de sa fonction barrière.',
    description:
      'La fonction barrière est assurée par le film hydrolipidique et les ' +
      'lipides intercornéocytaires. Lorsqu\'elle est fragilisée, la peau ' +
      'perd en eau, réagit plus facilement aux agressions extérieures et ' +
      'exprime des signes d\'inconfort : tiraillements, rougeurs, sensations ' +
      'de chaleur. Restaurer cette barrière est la première étape de toute ' +
      'routine efficace.',
    niveaux: {
      faible:      'Votre barrière cutanée semble globalement préservée.',
      modere:      'Votre peau présente quelques signes d\'inconfort à prendre en compte.',
      eleve:       'Votre fonction barrière mérite une attention particulière.',
      prioritaire: 'La restauration de votre confort cutané est notre priorité.'
    }
  },

  R: {
    id:     'R',
    label:  'Renouvellement & éclat',
    labelRoutine: 'le renouvellement cutané',
    labelNiveau: 'du renouvellement cutané',
    resume: 'Votre peau bénéficierait d\'un soutien de son renouvellement cellulaire.',
    description:
      'Le renouvellement cellulaire épidermique suit un cycle d\'environ ' +
      '28 jours. Avec le temps et les agressions oxydatives, ce rythme ' +
      'ralentit : le teint perd en luminosité, les taches persistent et ' +
      'la texture devient irrégulière. Les antioxydants et les actifs ' +
      'stimulants comme la vitamine C stabilisée jouent un rôle clé ' +
      'pour soutenir ce mécanisme.',
    niveaux: {
      faible:      'Votre renouvellement cellulaire semble actif.',
      modere:      'Quelques signes indiquent un ralentissement du renouvellement.',
      eleve:       'Votre éclat mérite un soutien ciblé.',
      prioritaire: 'Stimuler le renouvellement cellulaire est notre priorité.'
    }
  },

  S: {
    id:     'S',
    label:  'Séborégulation',
    labelRoutine: 'la séborégulation',
    labelNiveau: 'de la séborégulation',
    resume: 'Votre peau présente des signes de déséquilibre sébacé.',
    description:
      'La production de sébum est régulée par les glandes sébacées, ' +
      'sous l\'influence d\'androgènes et de facteurs environnementaux. ' +
      'Un excès favorise la dilatation des pores, l\'apparition ' +
      'd\'imperfections et une brillance persistante. ' +
      'L\'objectif n\'est pas de supprimer le sébum — il est indispensable ' +
      'à la protection cutanée — mais de rééquilibrer sa production.',
    niveaux: {
      faible:      'Votre équilibre sébacé semble satisfaisant.',
      modere:      'Quelques zones de brillance ou d\'imperfections sont présentes.',
      eleve:       'Votre peau bénéficierait d\'un rééquilibrage sébacé.',
      prioritaire: 'Rééquilibrer la production de sébum est notre priorité.'
    }
  },

  F: {
    id:     'F',
    label:  'Fermeté',
    labelRoutine: 'la fermeté',
    labelNiveau: 'de la fermeté',
    resume: 'Votre peau exprime un besoin de soutien de sa densité et de sa fermeté.',
    description:
      'La fermeté cutanée repose sur la qualité du réseau de collagène ' +
      'et d\'élastine dans le derme. Sous l\'effet du temps et des ' +
      'agressions oxydatives, ces fibres se dégradent progressivement : ' +
      'la peau perd en densité, en souplesse et en tonicité. ' +
      'Les actifs antioxydants et les peptides peuvent ralentir ' +
      'ce processus et soutenir la structure dermique.',
    niveaux: {
      faible:      'Votre densité cutanée semble bien préservée.',
      modere:      'Quelques signes de relâchement méritent attention.',
      eleve:       'Votre peau bénéficierait d\'un soutien de sa fermeté.',
      prioritaire: 'Soutenir la fermeté et la densité cutanée est notre priorité.'
    }
  },

  O: {
    id:     'O',
    label:  'Équilibre oxydatif',
    labelRoutine: "l'équilibre oxydatif",
    labelNiveau: "de l'équilibre oxydatif",
    resume: 'Votre peau bénéficierait d\'un soutien de ses défenses face au stress oxydatif.',
    description:
     'Au quotidien, la peau est exposée à de nombreuses sources de stress ' +
     'oxydatif : rayonnements UV, pollution, tabac ou encore certains ' +
     'facteurs liés au mode de vie. Ces agressions favorisent la formation ' +
      'de radicaux libres, capables d\'endommager progressivement différents ' +
'constituants des cellules cutanées, notamment les lipides, les protéines et l\'ADN. ' +
     'La peau dispose naturellement de systèmes de défense antioxydants, ' +
     'mais ceux-ci peuvent être dépassés lorsque les agressions deviennent ' +
     'trop importantes. Soutenir ces mécanismes contribue à préserver ' +
     'l\'équilibre et le bon fonctionnement de la peau.',
    niveaux: {
     faible:      'Vos défenses antioxydantes semblent globalement bien préservées.',
     modere:      'Votre peau est exposée à quelques facteurs susceptibles d\'augmenter le stress oxydatif.',
     eleve:       'Renforcer les défenses antioxydantes de votre peau pourrait être bénéfique.',
     prioritaire: 'Le soutien des défenses antioxydantes constitue l\'une des priorités de votre routine.'
    }
  }
}


// ============================================================
// SECTION 2 — INTRODUCTIONS (étape 1 : "Nous avons analysé")
// ============================================================

const INTRODUCTIONS = {
  equilibre:
    'Nous avons analysé vos réponses avec attention. ' +
    'Votre peau ne présente pas de besoin physiologique particulier en ce moment. ' +
    'C\'est une bonne nouvelle : l\'objectif est avant tout de préserver ' +
    'cet équilibre dans le temps grâce à une routine de prévention adaptée.',

  reactiveCosmetiques:
    'Nous avons analysé vos réponses avec attention. ' +
    'Votre peau réagit facilement aux cosmétiques — c\'est un signal ' +
    'que nous prenons en compte dans chaque choix de formule. ' +
    'Nous avons orienté votre routine vers le confort et la restauration ' +
    'de la fonction barrière, en privilégiant des actifs bien tolérés.',

  standard:
    'Nous avons analysé vos réponses avec attention. ' +
    'Voici une lecture personnalisée de l\'état de votre peau, ' +
    'construite à partir des signaux que vous nous avez transmis.'
}


// ============================================================
// SECTION 3 — DESCRIPTIONS PRODUITS
// ============================================================

const DESCRIPTIONS_PRODUITS = {
  'cocoon-creme': {
    titre:    'Crème Cocoon',
    accroche: 'Confort et restauration de la fonction barrière.',
    texte:
      'Formulée autour de la niacinamide, du panthénol et d\'actifs ' +
      'apaisants, la Crème Cocoon soutient la fonction barrière et ' +
      'réduit l\'inconfort cutané. Elle convient particulièrement aux ' +
      'peaux qui réagissent facilement ou qui expriment des tiraillements.',
    contextes: {
      barriere:
        'Votre peau exprime un besoin de confort et de protection. ' +
        'La Crème Cocoon est formulée pour restaurer une barrière cutanée ' +
        'fragilisée et réduire les réactions d\'inconfort.',
      equilibre:
        'En soin de prévention, la Crème Cocoon maintient le confort ' +
        'cutané et renforce la résistance de la peau aux agressions quotidiennes.' ,
      preparateur_maquillage:
        'La Crème Cocoon aide à préserver l\'hydratation et le confort des zones qui ont tendance à ' +
        'marquer sous le maquillage. En améliorant progressivement la souplesse et la régularité de ' +
        'la surface cutanée, elle contribue à favoriser un rendu du maquillage plus uniforme.'
    }
  },

  'peps-creme': {
    titre:    'Crème PEP\'S',
    accroche: 'Éclat, prévention et soutien antioxydant.',
    texte:
      'Enrichie en vitamine C stabilisée, vitamine E et astaxanthine, ' +
      'la Crème PEP\'S agit sur l\'éclat, le renouvellement cellulaire ' +
      'et la fermeté. Sa stratégie antioxydante complète en fait ' +
      'un soin de prévention efficace contre le vieillissement cutané.',
    contextes: {
      eclat_fermete:
        'Votre peau bénéficierait d\'un soutien antioxydant ciblé. ' +
        'La Crème PEP\'S est notre soin de référence pour soutenir ' +
        'l\'éclat, le renouvellement et la fermeté cutanée.',
      prevention:
        'Votre peau fonctionne bien. La Crème PEP\'S accompagne ' +
        'une peau équilibrée en soutenant ses mécanismes physiologiques ' +
        'dans le temps, avant l\'apparition des premiers signes.',
      equilibre:
        'En l\'absence de besoin prioritaire identifié, la Crème PEP\'S ' +
        'est notre soin de prévention : elle préserve l\'éclat et la qualité ' +
        'cutanée d\'une peau qui fonctionne déjà bien.',
      oxydatif: 
        'Votre peau bénéficierait d\'un soutien antioxydant ciblé. ' +
        'La Crème PEP\'S est notre soin de référence pour renforcer ses défenses.',
      seborregulatrice: 
        'Votre peau présente des signes de déséquilibre sébacé. ' +
        'La Crème PEP\'S accompagne votre routine en soutenant l\'éclat ' +
        'et la qualité cutanée, en complément du soin purifiant hebdomadaire.'

    }
  },

  'peps-masque': {
    titre:    'Masque PEP\'S',
    accroche: 'Éclat intensif et renouvellement en une étape.',
    texte:
      'Masque, gommage doux et nettoyant en un seul geste. ' +
      'Enrichi en vitamine C et en actifs exfoliants doux, ' +
      'le Masque PEP\'S amplifie l\'éclat et affine le grain de peau ' +
      'en une application hebdomadaire.',
    contextes: {
      eclat:
        'Pour amplifier l\'action de votre routine quotidienne, ' +
        'le Masque PEP\'S apporte un boost d\'éclat et de renouvellement ' +
        'en une application hebdomadaire.' ,
      stress:
        'Riche en actifs antioxydants, le Masque PEP\'S complète votre routine tout en offrant ' +
        'un moment de soin enveloppant, porté par ses notes d\'orange douce. Une fois par semaine, ' +
        'son temps de pose peut devenir une occasion de s\'accorder quelques minutes de pause.',
    }
  },

  'pure-peeling': {
    titre:    'Peeling PURE',
    accroche: 'Purification et rééquilibrage sébacé.',
    texte:
      'Formulé à l\'acide salicylique, le Peeling PURE agit en profondeur ' +
      'pour désincruster les pores, réguler la production de sébum ' +
      'et réduire l\'apparence des imperfections. ' +
      'Son action kératolytique douce le rend compatible avec une ' +
      'utilisation régulière.',
    contextes: {
      equilibre:
        'Votre peau présente des signes de déséquilibre sébacé. ' +
        'Le Peeling PURE est notre soin de référence pour purifier ' +
        'les pores et rééquilibrer la production de sébum en profondeur.'
    }
  },

  'velvet-mousse': {
    titre:    'Mousse Velvet',
    accroche: 'Nettoyage doux, respect de la barrière cutanée.',
    texte:
      'Formulée à la rose de Damas et à des tensioactifs doux, ' +
      'la Mousse Velvet nettoie efficacement tout en préservant ' +
      'le film hydrolipidique. Elle convient aux peaux sensibles, ' +
      'réactives ou orientées barrière.',
    contextes: {
      nettoyage:
        'Adaptée aux peaux sensibles ou fragilisées, ' +
        'la Mousse Velvet assure un nettoyage respectueux ' +
        'qui ne compromet pas la fonction barrière.'
    }
  },

  'peps-mousse': {
    titre:    'Mousse PEP\'S',
    accroche: 'Nettoyage vitaminé et soutien antioxydant.',
    texte:
    'La Mousse PEP\'S associe un nettoyage doux à des actifs antioxydants pour ' +
    'accompagner la peau dès la première étape de la routine.',
    contextes: {
    nettoyage:
      'La Mousse PEP\'S associe un nettoyage doux à des actifs antioxydants pour ' +
      'accompagner la peau dès la première étape de la routine.',
  }
},


  'pure-oleogel': {
    titre:    'Oléogel PURE',
    accroche: 'Démaquillage waterproof par affinité lipidique.',
    texte:
      'L\'Oléogel PURE dissout les maquillages résistants — y compris ' +
      'les formules waterproof — par affinité lipidique, sans frottement ' +
      'ni résidu. Sa formule anhydre le rend particulièrement stable ' +
      'et adapté aux voyages.',
    contextes: {
     nettoyage:
      'Vous utilisez des produits waterproof ou longue tenue. ' +
      'L\'Oléogel PURE est la solution la plus efficace et la plus douce ' +
      'pour les éliminer complètement, sans altérer la barrière cutanée.',
     nettoyage_doux_imperfections:
      'Votre peau présente des imperfections inflammatoires. ' +
      'L\'Oléogel PURE nettoie en douceur par affinité lipidique, ' +
      'sans friction ni résidu, pour ne pas aggraver les zones sensibles.',
     nettoyage_waterproof_imperfections:
      'Vous avez indiqué préférer un nettoyant sans rinçage — nous avons ' +
      'respecté ce choix. Néanmoins, votre profil cutané (imperfections ' +
      'inflammatoires et/ou maquillage waterproof) pourrait davantage bénéficier ' +
      'de l\'Oléogel PURE : il dissout en douceur par affinité lipidique, ' +
      'sans friction ni résidu, ce qui limite l\'irritation des zones sensibles.'
    }   
  },

  'cocoon-baume-corps': {
    titre:    'Baume Réparateur Corps Cocoon',
    accroche: 'Nutrition intense et réparation pour le corps.',
    texte:
      'Enrichi en amande douce, karité et actifs réparateurs, ' +
      'le Baume Cocoon nourrit en profondeur et restaure le confort ' +
      'des peaux sèches, rugueuses ou inconfortables. ' +
      'Sa texture fondante pénètre rapidement sans laisser de film gras.',
    contextes: {
      corps:
        'Votre peau exprime des besoins spécifiques au niveau du corps. ' +
        'Le Baume Réparateur Cocoon apporte nutrition intense et confort ' +
        'durable, adapté aux peaux sèches ou inconfortables.'
    }
  }
}


// ============================================================
// SECTION 4 — ARTICLES BLOG
// ============================================================

const ARTICLES_BLOG = [
  // Pilier B
  {
    id: 'article-barriere',
    piliers: ['B'],
    titre: 'Barrière cutanée : comprendre l\'équilibre vivant de la peau',
    url: '/blogs/kosmorebi-science/barriere-cutanee-comprendre-et-preserver-votre-premiere-ligne-de-defense'
  },
  {
    id: 'article-microbiome',
    piliers: ['B'],
    titre: 'Le microbiome cutané : un écosystème au cœur de l\'équilibre de la peau',
    url: '/blogs/actualites/le-microbiome-cutane-un-ecosysteme-au-coeur-de-lequilibre-de-la-peau'
  },
  {
    id: 'article-hydratation',
    piliers: ['B'],
    titre: 'Hydrater sa peau : les bons gestes et les idées reçues',
    url: '/blogs/conseils-beaute/hydrater-sa-peau-bons-gestes-fausses-idees'
  },
  {
    id: 'article-niacinamide',
    piliers: ['B'],
    titre: 'Niacinamide & Panthénol : le duo réparateur',
    url: '/blogs/nos-ingredients/niacinamide-panthenol-le-duo-reparateur-au-coeur-de-nos-formules'
  },
  // Pilier O
  {
    id: 'article-oxydatif',
    piliers: ['O'],
    titre: 'Stress oxydatif et vieillissement cutané : comprendre le rôle des radicaux libres',
    url: '/blogs/actualites/stress-oxydatif-vieillissement-cutane-radicaux-libres'
  },
  {
    id: 'article-inflammaging',
    piliers: ['O'],
    titre: 'Inflamm\'aging : quand l\'inflammation silencieuse accélère le vieillissement cutané',
    url: '/blogs/kosmorebi-science/inflammaging-quand-linflammation-silencieuse-accelere-le-vieillissement-cutane'
  },
  {
    id: 'article-vitc-vite',
    piliers: ['O'],
    titre: 'Vitamine C, E et astaxanthine : pourquoi les associer ?',
    url: '/blogs/nos-ingredients/vitamine-c-vitamine-e-astaxanthine-le-trio-antioxydant-de-la-gamme-peps'
  },
  {
    id: 'article-vitc-eclat',
    piliers: ['O', 'R'],
    titre: 'Les vertus de la vitamine C pour l\'éclat',
    url: '/blogs/conseils-beaute/skincare-a-lorange-les-vertus-de-la-vitamine-c-pour-leclat'
  },
  // Pilier R
  {
    id: 'article-structure-peau',
    piliers: ['R'],
    titre: 'La peau : structure, fonctions et organisation',
    url: '/blogs/kosmorebi-science/structure-fonctionnement-peau'
  },
  {
    id: 'article-gluconolactone',
    piliers: ['R'],
    titre: 'Gluconolactone : un actif d\'équilibre entre renouvellement et respect de la peau',
    url: '/blogs/nos-ingredients/gluconolactone-exfoliant-nouvelle-generation-equilibre-cutane'
  },
  {
    id: 'article-vitb12',
    piliers: ['R'],
    titre: 'La vitamine B12 : l\'actif méconnu qui prend soin de votre peau',
    url: '/blogs/nos-ingredients/la-vitamine-b12-l-actif-meconnu-qui-prend-soin-de-votre-peau'
  },
  {
    id: 'article-exfoliation',
    piliers: ['R'],
    titre: 'Pourquoi exfolier sa peau : le rôle de l\'exfoliation dans votre routine skincare',
    url: '/blogs/conseils-beaute/pourquoi-exfolier-sa-peau-role-exfoliation-routine'
  },
  {
    id: 'article-peeling-gommage',
    piliers: ['R', 'S'],
    titre: 'Peeling ou gommage : comment choisir la bonne exfoliation pour votre peau ?',
    url: '/blogs/conseils-beaute/peeling-ou-gommage-comment-choisir-la-bonne-exfoliation-pour-votre-peau'
  },
  // Pilier S
  {
    id: 'article-salicylique',
    piliers: ['S'],
    titre: 'L\'acide salicylique : l\'allié des peaux à tendance grasse et à imperfections',
    url: '/blogs/nos-ingredients/lacide-salicylique-lallie-des-peaux-a-tendance-acneique'
  },
  {
    id: 'article-peau-grasse-hydratation',
    piliers: ['S'],
    titre: 'Pourquoi une peau grasse a aussi besoin d\'hydratation',
    url: '/blogs/conseils-beaute/pourquoi-une-peau-grasse-a-aussi-besoin-d-hydratation'
  },
  {
    id: 'article-comedogenes',
    piliers: ['S'],
    titre: 'Les ingrédients comédogènes',
    url: '/blogs/conseils-beaute/les-ingredients-comedogenes'
  },
  // Pilier F
  {
    id: 'article-vieillissement',
    piliers: ['F'],
    titre: 'Vieillissement cutané : mécanismes biologiques et skin longevity',
    url: '/blogs/kosmorebi-science/vieillissement-cutane-mecanismes-skin-longevity'
  },
  {
    id: 'article-cellules-zombies',
    piliers: ['F'],
    titre: 'Cellules zombies : une nouvelle lecture du vieillissement cutané',
    url: '/blogs/kosmorebi-science/cellules-zombies-et-vieillissement-cutane-comment-sen-debarrasser-naturellement'
  },
  {
    id: 'article-glycation',
    piliers: ['F'],
    titre: 'La glycation cutanée : comprendre l\'altération progressive du collagène et de l\'élastine',
    url: '/blogs/kosmorebi-science/glycation-mecanisme-vieillissement-cutane'
  },
  // Articles conditionnels
  {
    id: 'article-peau-sensible',
    piliers: [],
    conditionnel: 'peauSensible',
    titre: 'Peau sensible : de quoi parle-t-on vraiment ?',
    url: '/blogs/conseils-beaute/peau-sensible-de-quoi-parle-t-on-vraiment'
  },
  {
    id: 'article-stress',
    piliers: [],
    conditionnel: 'stress',
    titre: 'Comment le stress influence les besoins de votre peau',
    url: '/blogs/conseils-beaute/comment-le-stress-influence-les-besoins-de-votre-peau'
  },
  {
    id: 'article-tiraillement',
    piliers: [],
    conditionnel: 'tiraillement',
    titre: 'Pourquoi votre peau tiraille après le nettoyage ?',
    url: '/blogs/conseils-beaute/pourquoi-votre-peau-tiraille-apres-le-nettoyage'
  },
  {
    id: 'article-uv-photovieillissement',
    piliers: [],
    conditionnel: 'soleil',
    titre: 'Les effets des UV sur la peau : comprendre les mécanismes biologiques du photovieillissement',
    url: '/blogs/kosmorebi-science/les-effets-des-uv-sur-la-peau-comprendre-les-mecanismes-biologiques-du-photovieillissement'
  },

  {
    id: 'article-uv',
    piliers: [],
    conditionnel: 'soleil',
    titre: 'Protection solaire : les erreurs les plus fréquentes',
    url: '/blogs/conseils-beaute/protection-solaire-les-erreurs-frequentes-et-les-bons-gestes-pour-proteger-sa-peau-efficacement'
  },
  {
    id: 'article-secheresse',
    piliers: [],
    conditionnel: 'secheresse',
    titre: 'Peau de croco, jambes sèches, zones rugueuses : quand une crème ne suffit plus',
    url: '/blogs/conseils-beaute/peau-de-croco-jambes-seches-zones-rugueuses-baume-corps'
  },

]


// ============================================================
// SECTION 5 — CONCLUSIONS
// ============================================================

const CONCLUSIONS = {
  equilibre:
    'Votre peau est votre meilleur atout. ' +
    'Une routine simple, régulière et bien formulée suffit à préserver ' +
    'cet équilibre. Si vous avez des questions sur votre routine, ' +
    'notre équipe est disponible pour vous accompagner.',

  reactiveCosmetiques:
    'La régularité et la douceur sont vos meilleures alliées. ' +
    'Une fois le confort restauré, votre routine pourra évoluer ' +
    'progressivement vers des soins plus ciblés. ' +
    'N\'hésitez pas à nous contacter si vous souhaitez être accompagnée.',

  standard:
    'Votre routine est maintenant construite sur une lecture précise ' +
    'de vos besoins cutanés. Si vous avez des questions ou souhaitez ' +
    'affiner votre diagnostic, notre équipe est disponible pour vous répondre.'
}


// ============================================================
// SECTION 6 — SEUILS D'AFFICHAGE DES NIVEAUX
// ============================================================

const SEUILS_NIVEAUX = {
  prioritaire: 2.5,  // score < 2.5 → besoin prioritaire
  eleve:       4.5,  // score < 4.5 → besoin fortement marqué
  marque:      7.0,  // score < 7.0 → besoin présent
                     // score >= 7.0 → fonction préservée
}

function getNiveau(score) {
  if (score < SEUILS_NIVEAUX.prioritaire) return 'prioritaire'
  if (score < SEUILS_NIVEAUX.eleve)       return 'eleve'
  if (score < SEUILS_NIVEAUX.marque)      return 'modere'
  return 'faible'   // score >= 7 → fonction préservée
}


// ============================================================
// FONCTIONS DE RENDU
// ============================================================

function renderIntroduction(profils) {
  if (profils.equilibre)           return INTRODUCTIONS.equilibre
  if (profils.reactiveCosmetiques) return INTRODUCTIONS.reactiveCosmetiques
  return INTRODUCTIONS.standard
}

function renderPiliers(normalises) {
  return Object.keys(PILIERS).map(cle => {
    const pilier    = PILIERS[cle]
    const needScore = normalises[cle] || 0
    const score = Math.round(needScore * 10) / 10
    const niveau    = getNiveau(needScore)
    return {
      id:          cle,
      label:       pilier.label,
      icon:        pilier.icon,
      score,
      niveau,
      description: pilier.description,
      message:     pilier.niveaux[niveau]
    }
  })
}

function renderSynthese(profils, normalises) {
  // Paramètres configurables
  const SCORE_EQUILIBRE    = 7.0
  const ECART_HOMOGENE     = 1.0
  const ECART_PRIORITAIRE  = 0.8
  const ECART_ASSOCIE      = 1.5


  const piliers = Object.keys(normalises)
  const scores  = piliers.map(p => normalises[p])
  const scoreMin = Math.min(...scores)
  const scoreMax = Math.max(...scores)
  const amplitude = scoreMax - scoreMin

  const tries = piliers
    .map(p => ({
      cle:          p,
      score:        normalises[p],
      label:        PILIERS[p]?.label ?? p,
      labelRoutine: PILIERS[p]?.labelRoutine ?? PILIERS[p]?.label ?? p,
      labelNiveau:  PILIERS[p]?.labelNiveau ?? PILIERS[p]?.labelRoutine ?? PILIERS[p]?.label ?? p
    }))
    .sort((a, b) => a.score - b.score)


  // Cas 1 — Profil équilibré : tous les scores >= 7
  if (scoreMin >= SCORE_EQUILIBRE) {
    return 'Votre peau présente un profil globalement équilibré, sans besoin particulièrement marqué. ' +
           'Une routine légère et régulière suffit à maintenir cet équilibre et à préserver la santé de votre peau dans la durée.'
  }

  // Cas 2 — Profil homogène mais globalement bas : amplitude <= 1 et scoreMin < 7
  if (amplitude <= ECART_HOMOGENE) {
    return 'Votre diagnostic met en évidence un besoin global d\'accompagnement de l\'ensemble des fonctions de votre peau. ' +
           'Aucune fonction ne ressort de façon isolée : c\'est l\'équilibre général qui mérite d\'être soutenu. ' +
           'La routine proposée adopte donc une approche globale, cohérente et progressive.'
  }

  // Cas 3 — Répartition hiérarchique
  const prioritaires = tries.filter(p => p.score - scoreMin <= ECART_PRIORITAIRE)
  if (prioritaires.length >= 4) {
    return 'Votre diagnostic met en évidence plusieurs besoins qui concernent simultanément ' +
           'différentes fonctions de votre peau. Plutôt qu\'un déséquilibre isolé, il suggère ' +
           'que plusieurs mécanismes physiologiques sont actuellement sollicités. ' +
           'La routine proposée adopte donc une approche globale pour accompagner ' +
           'ces différents besoins de manière cohérente.'
  }

  const associes     = tries.filter(p => p.score - scoreMin >  ECART_PRIORITAIRE &&
                                         p.score - scoreMin <= ECART_ASSOCIE)
  const preserves    = tries.filter(p => p.score - scoreMin >  ECART_ASSOCIE)

  // Construction du texte
  const labelsPrio = prioritaires.map(p => p.labelRoutine)
  const labelsPrioNiveau = prioritaires.map(p => p.labelNiveau)
  const labelsAssoc = associes.map(p => p.labelRoutine)
  const labelsPreserv = preserves.map(p => p.labelRoutine)

  function joinLabels(labels) {
    if (labels.length === 1) return labels[0]
    return labels.slice(0, -1).join(', ') + ' et ' + labels[labels.length - 1]
  }

  let texte = ''

  // Phrase 1 — prioritaires

  if (labelsPrioNiveau.length === 1) {
   texte += `Votre diagnostic met principalement en évidence un besoin au niveau ${labelsPrioNiveau[0]}.`
  } else {
   const dernierPrio = labelsPrioNiveau[labelsPrioNiveau.length - 1]
   const autresPrio  = labelsPrioNiveau.slice(0, -1).join(', ')
   texte += `Votre diagnostic met principalement en évidence des besoins au niveau ${autresPrio} et ${dernierPrio}.`
  }


  // Phrase 2 — associés (si présents)
    const GENRE_PILIER = { B: 'f', R: 'm', S: 'f', F: 'f', O: 'm' }

  if (labelsAssoc.length > 0) {
    const accord = labelsAssoc.length === 1
      ? (GENRE_PILIER[associes[0].cle] === 'f' ? 'prise en compte' : 'pris en compte')
      : 'pris en compte'
    const label = joinLabels(labelsAssoc)
    texte += ` ${label.charAt(0).toUpperCase() + label.slice(1)} ${labelsAssoc.length === 1 ? 'est également' : 'sont également'} ${accord} dans la routine proposée.`
  }


  // Phrase 3 — préservés (si présents)
  if (labelsPreserv.length > 0) {
    texte += ` ${joinLabels(labelsPreserv).charAt(0).toUpperCase() + joinLabels(labelsPreserv).slice(1)} ${labelsPreserv.length === 1 ? 'apparaît' : 'apparaissent'} globalement mieux préservé${labelsPreserv.length === 1 ? '' : 's'}.`
  }

  // Phrase de clôture
  texte += ' La routine proposée tient compte de cette hiérarchie afin d\'accompagner votre peau de manière cohérente.'

  return texte
}

function renderRecommandations(recommandations) {
  const categories = ['nettoyage', 'soinsVisage', 'soinsHebdomadaires', 'corps']
  const labelsCategories = {
    soinsVisage:        'Soins visage',
    soinsHebdomadaires: 'Soins hebdomadaires',
    nettoyage:          'Nettoyage',
    corps:              'Soin du corps'
  }
  return categories
    .filter(cat => recommandations[cat] && recommandations[cat].length > 0)
    .map(cat => ({
      categorie: cat,
      label:     labelsCategories[cat],
      produits:  recommandations[cat].filter(Boolean).map(p => renderProduit(p))
    }))
}

function renderProduit(produit) {
  const config   = DESCRIPTIONS_PRODUITS[produit.id]
  const contexte = produit.contexte
    if (produit.id === 'peps-creme') {
    console.log('[peps-creme]', { contexte, contextes: Object.keys(config?.contextes || {}) })
  }
  if (!config) {
    return {
      id:       produit.id,
      handle:   produit.handle,
      url:      `/products/${produit.handle}`,
      image: produit.image || null,
      titre:    produit.title,
      accroche: '',
      texte:    'Description à compléter.',
      routine:  renderRoutine(produit.routine)
    }
  }
  return {
    id:       produit.id,
    handle:   produit.handle,
    url:      `/products/${produit.handle}`,
    image: config.image || produit.image || null,
    titre:    config.titre,
    accroche: config.accroche,
    texte:    config.contextes?.[contexte] || config.texte,
    routine:  renderRoutine(produit.routine)
  }
}

// ============================================================
// SECTION 6 — NOTES CONTEXTUELLES PHYSIOLOGIQUES
// ============================================================

const NOTES_CONTEXTUELLES = [

  {
    id:       'multi_bonne_pratique',
    position: 'avant',
    priorite: 8,
    remplace: [],
    conditions: {
      prioritaires:           [],
      prioritairesOuAssocies: [],
      minPiliersConcernes:    3
    },
    titre: 'Bon à savoir',
    theme: 'Profil multi-mécanismes — stratégie',
    texte:
      'Quand plusieurs mécanismes sont déséquilibrés en même temps, vouloir corriger ' +
      'chaque symptôme séparément est souvent une mauvaise stratégie. Plutôt que ' +
      'd\'accumuler les soins très ciblés, privilégiez une routine simple, composée ' +
      'de soins capables de soutenir plusieurs fonctions de la peau.'
  },

  {
    id:       'multi_simplicite',
    position: 'avant',
    priorite: 7,
    remplace: [],
    conditions: {
      prioritaires:           [],
      prioritairesOuAssocies: [],
      minPiliersConcernes:    3
    },
    titre: 'Conseil pratique',
    theme: 'Profil multi-mécanismes — observance',
    texte:
      'Une routine n\'est utile que si elle s\'intègre durablement dans votre quotidien. ' +
      'Des soins dont la formulation prend en compte plusieurs fonctions de la peau permettent ' +
      'souvent de limiter le nombre d\'étapes, sans multiplier les produits. La routine ' +
      'reste ainsi plus facile à suivre, même lorsque vous manquez de temps ou êtes ' +
      'en déplacement.'
  },

]



const LABELS_MOMENTS = {
  matin:        'Le matin',
  soir:         'Le soir',
  matin_et_soir:'Matin et soir'
}

function renderRoutine(routine) {
  if (!routine) return null
  return {
    moment:    LABELS_MOMENTS[routine.moment] || routine.moment,
    frequence: routine.frequence || null,
    note:      routine.note || null
  }
}

function renderNotes(notes) {
  if (!notes || notes.length === 0) return [];
  return notes;
}

function renderArticles(articles) {
  if (!articles) return { pilier: [], conditionnel: [] }
  return {
    pilier:       articles.pilier       || [],
    conditionnel: articles.conditionnel || []
  }
}

function renderConclusion(profils) {
  if (profils.equilibre)           return CONCLUSIONS.equilibre
  if (profils.reactiveCosmetiques) return CONCLUSIONS.reactiveCosmetiques
  return CONCLUSIONS.standard
}


// ============================================================
// FONCTION PRINCIPALE
// ============================================================

function buildDiagnosticContent(result) {
  if (!result || !result.scores || !result.profils || !result.recommandations) {
    throw new Error('[diagnostic-content] result invalide.')
  }
  const { normalises }                                = result.scores
  const { profils, recommandations, notes, articles } = result
  return {
    introduction:    renderIntroduction(profils),
    piliers:         renderPiliers(normalises),
    synthese:        renderSynthese(profils, normalises),
    recommandations: renderRecommandations(recommandations),
    notes: (() => {
  const ordre = ['Bon à savoir', 'Conseil soin', 'Conseil nettoyage', 'Conseil hydratation', 'Conseil maquillage', 'Conseil pratique']
  const fusion = [...renderNotes(notes), ...selectionnerNotes(normalises)]
  fusion.sort((a, b) => {
    const ia = ordre.indexOf(a.titre)
    const ib = ordre.indexOf(b.titre)
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
  })
  return fusion
})(),
    articles:        renderArticles(articles),
    conclusion:      renderConclusion(profils),
    introRoutine:    genererIntroRoutine(normalises),
  }
}

function analyserPrioritesPiliers(normalises) {
  const SCORE_EQUILIBRE  = 7.0
  const ECART_COPRIORITE = 0.8
  const ECART_DOMINANT   = 1.5

  if (!normalises || typeof normalises !== 'object') return null
  const entrees = Object.entries(normalises).filter(([, v]) => typeof v === 'number' && isFinite(v))
  if (entrees.length === 0) return null

  const scoreMin = Math.min(...entrees.map(([, v]) => v))

  if (scoreMin >= SCORE_EQUILIBRE) {
    return { profilEquilibre: true, prioritaires: [], associes: [], secondaires: [] }
  }

  const tries = entrees
    .map(([cle, score]) => ({ cle, score, labelRoutine: PILIERS[cle]?.labelRoutine ?? cle }))
    .sort((a, b) => a.score - b.score)

  return {
    profilEquilibre: false,
    prioritaires: tries.filter(p => p.score - scoreMin <= ECART_COPRIORITE),
    associes:     tries.filter(p => p.score - scoreMin >  ECART_COPRIORITE &&
                                    p.score - scoreMin <= ECART_DOMINANT),
    secondaires:  tries.filter(p => p.score - scoreMin >  ECART_DOMINANT)
  }
}

function selectionnerNotes(normalises) {
  const analyse = analyserPrioritesPiliers(normalises)
  if (!analyse) return []

  const { profilEquilibre, prioritaires, associes } = analyse
  const piliersPrio  = new Set(prioritaires.map(p => p.cle))
  const piliersAssoc = new Set(associes.map(p => p.cle))


  function noteEligible(note) {
    if (note.conditions.reponses) return false
    if (profilEquilibre) return false

    const {
      prioritaires:           cp  = [],
      prioritairesOuAssocies: cpa = [],
      minPrioritaires,
      minPiliersConcernes
    } = note.conditions

  if (
    minPrioritaires !== undefined &&
    prioritaires.length < minPrioritaires
  ) {
    return false
  }

  if (
    minPiliersConcernes !== undefined &&
    prioritaires.length + associes.length < minPiliersConcernes
  ) {
    return false
  }

  return (
    cp.every(p  => piliersPrio.has(p)) &&
    cpa.every(p => piliersPrio.has(p) || piliersAssoc.has(p))
  )
}



  const candidates = NOTES_CONTEXTUELLES
    .filter(noteEligible)
    .sort((a, b) => b.priorite - a.priorite)


  const exclues  = new Set()
  const retenues = []
  for (const note of candidates) {
    if (exclues.has(note.id)) continue
    retenues.push(note)
    note.remplace.forEach(id => exclues.add(id))
  }

  return retenues
}

function genererIntroRoutine(normalises) {
  const analyse = analyserPrioritesPiliers(normalises)
  if (!analyse) return ''

  const { profilEquilibre, prioritaires, associes } = analyse

  if (profilEquilibre) {
    return 'Votre diagnostic ne met pas en évidence de déséquilibre majeur. ' +
           'Nous avons donc privilégié une routine simple, destinée à accompagner ' +
           'les mécanismes naturels de votre peau et à préserver durablement son équilibre.'
  }

  function joinLabels(items) {
    const labels = items.map(p => p.labelRoutine)
    if (labels.length === 1) return labels[0]
    return labels.slice(0, -1).join(', ') + ' et ' + labels[labels.length - 1]
  }

  function majuscule(str) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

    const GENRE_PILIER = { B: 'f', R: 'm', S: 'f', F: 'f', O: 'm' }
  const labelAssoc   = associes.length > 0 ? joinLabels(associes) : null
  const accordAssoc  = associes.length === 1
    ? (GENRE_PILIER[associes[0].cle] === 'f' ? 'prise' : 'pris')
    : (associes.every(p => GENRE_PILIER[p.cle] === 'f') ? 'prises' : 'pris')
  const suffixeAssoc = labelAssoc
    ? ` ${majuscule(labelAssoc)} ${associes.length === 1 ? 'a' : 'ont'} également été ${accordAssoc} en compte dans la sélection des soins.`
    : ''


  if (prioritaires.length === 1) {
    const label = majuscule(prioritaires[0].labelRoutine)
    return `${label} constitue actuellement le besoin prioritaire mis en évidence par votre diagnostic. ` +
           `Nous avons sélectionné des soins adaptés pour accompagner en priorité cette fonction, ` +
           `tout en tenant compte de l'équilibre global de votre peau.${suffixeAssoc}`
  }

  if (prioritaires.length === 2) {
    const labelPrio = joinLabels(prioritaires)
    return `${majuscule(labelPrio)} constituent les deux besoins prioritaires mis en évidence par votre diagnostic. ` +
           `Nous avons sélectionné des soins capables d'accompagner ces deux fonctions de manière complémentaire, ` +
           `tout en tenant compte de l'équilibre global de votre peau.${suffixeAssoc}`
  }

  const labelPrio = joinLabels(prioritaires)
  return `Votre diagnostic met en évidence plusieurs besoins prioritaires : ${labelPrio}. ` +
         `Nous avons sélectionné des soins capables d'accompagner simultanément ces différentes fonctions ` +
         `afin de construire une routine cohérente et adaptée à votre profil.${suffixeAssoc}`
}
